import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#6B7B3F] rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-heading font-bold text-[#292524]">
              {lang === 'hr' ? 'Politika Privatnosti' : 'Privacy Policy'}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-10 space-y-8">
          
          <div className="text-sm text-stone-500 mb-6">
            {lang === 'hr' ? 'Datum stupanja na snagu: 3. ožujka 2026.' : 'Effective Date: March 3, 2026'}
          </div>

          {lang === 'hr' ? (
            <>
              {/* Croatian Version */}
              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">1. Uvod</h2>
                <p className="text-stone-600 leading-relaxed">
                  Dobrodošli u Bereket ("mi", "nas" ili "naš"). Cijenimo vašu privatnost i posvećeni smo zaštiti vaših osobnih podataka. 
                  Ova Politika privatnosti objašnjava kako prikupljamo, koristimo, pohranjujemo i štitimo vaše informacije kada koristite 
                  našu web aplikaciju Bereket.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">2. Podaci koje prikupljamo</h2>
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-[#292524]">2.1 Podaci o računu</h3>
                  <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                    <li>Email adresa</li>
                    <li>Ime (ako ga navedete)</li>
                    <li>Podaci za autentifikaciju putem Google prijave (ako koristite tu opciju)</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-[#292524]">2.2 Podaci o korištenju</h3>
                  <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                    <li>Zapisi o obavljenim namazima</li>
                    <li>Bereket bodovi i aktivnosti</li>
                    <li>Zikr brojači</li>
                    <li>Dnevnički zapisi</li>
                    <li>Planer događaji</li>
                    <li>Napredak u učenju Kur'ana</li>
                    <li>Sadaka zapisi</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-[#292524]">2.3 Tehnički podaci</h3>
                  <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                    <li>Lokacija (samo za izračun vremena namaza i kiblu, uz vaš pristanak)</li>
                    <li>Podaci o push notifikacijama (ako ih omogućite)</li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">3. Kako koristimo vaše podatke</h2>
                <p className="text-stone-600 leading-relaxed">Vaše podatke koristimo za:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Pružanje i održavanje naše usluge</li>
                  <li>Personalizaciju vašeg duhovnog iskustva</li>
                  <li>Praćenje vašeg duhovnog napretka</li>
                  <li>Slanje podsjetnika za namaz (ako ih omogućite)</li>
                  <li>Prikaz točnih vremena namaza za vašu lokaciju</li>
                  <li>Poboljšanje naše aplikacije</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">4. Pohrana i sigurnost podataka</h2>
                <p className="text-stone-600 leading-relaxed">
                  Vaši podaci pohranjuju se sigurno putem Supabase platforme koja koristi enkripciju i moderne sigurnosne standarde. 
                  Implementiramo odgovarajuće tehničke i organizacijske mjere za zaštitu vaših osobnih podataka od neovlaštenog pristupa, 
                  gubitka ili uništenja.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">5. Dijeljenje podataka</h2>
                <p className="text-stone-600 leading-relaxed">
                  <strong>Ne prodajemo niti dijelimo vaše osobne podatke s trećim stranama</strong> osim u sljedećim slučajevima:
                </p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Pružatelji usluga koji nam pomažu u radu aplikacije (Supabase za bazu podataka, Aladhan API za vremena namaza)</li>
                  <li>Kada je to zakonski obvezno</li>
                  <li>Uz vaš izričiti pristanak</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">6. Google prijava</h2>
                <p className="text-stone-600 leading-relaxed">
                  Ako se odlučite prijaviti putem Google računa, primamo ograničene informacije iz vašeg Google profila 
                  (email adresa i ime). Pristupamo samo podacima potrebnim za autentifikaciju i ne pristupamo vašim 
                  kontaktima, kalendarima ili drugim Google uslugama.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">7. Vaša prava</h2>
                <p className="text-stone-600 leading-relaxed">Imate pravo:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Pristupiti svojim osobnim podacima</li>
                  <li>Ispraviti netočne podatke</li>
                  <li>Zatražiti brisanje svojih podataka</li>
                  <li>Povući pristanak za obradu podataka</li>
                  <li>Izvesti svoje podatke</li>
                </ul>
                <p className="text-stone-600 leading-relaxed mt-4">
                  Za ostvarivanje ovih prava, kontaktirajte nas na email naveden u odjeljku "Kontakt".
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">8. Kolačići i lokalna pohrana</h2>
                <p className="text-stone-600 leading-relaxed">
                  Koristimo lokalnu pohranu preglednika za spremanje vaših postavki i sesije. Ne koristimo kolačiće za praćenje 
                  ili oglašavanje.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">9. Promjene ove politike</h2>
                <p className="text-stone-600 leading-relaxed">
                  Možemo povremeno ažurirati ovu Politiku privatnosti. O značajnim promjenama obavijestit ćemo vas putem 
                  aplikacije ili emaila.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">10. Kontakt</h2>
                <p className="text-stone-600 leading-relaxed">
                  Ako imate pitanja o ovoj Politici privatnosti ili o tome kako postupamo s vašim podacima, 
                  kontaktirajte nas na:
                </p>
                <div className="bg-stone-50 p-4 rounded-xl mt-4">
                  <p className="text-stone-700 font-medium">Email: app@islam-iznutra.com</p>
                </div>
              </section>
            </>
          ) : (
            <>
              {/* English Version */}
              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">1. Introduction</h2>
                <p className="text-stone-600 leading-relaxed">
                  Welcome to Bereket ("we", "us", or "our"). We value your privacy and are committed to protecting your personal data. 
                  This Privacy Policy explains how we collect, use, store, and protect your information when you use our Bereket web application.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">2. Information We Collect</h2>
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-[#292524]">2.1 Account Information</h3>
                  <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                    <li>Email address</li>
                    <li>Name (if provided)</li>
                    <li>Google Sign-In authentication data (if you use this option)</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-[#292524]">2.2 Usage Data</h3>
                  <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                    <li>Prayer completion records</li>
                    <li>Barakah points and activities</li>
                    <li>Dhikr counters</li>
                    <li>Journal entries</li>
                    <li>Planner events</li>
                    <li>Quran reading progress</li>
                    <li>Sadaqah records</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-[#292524]">2.3 Technical Data</h3>
                  <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                    <li>Location (only for prayer times and qibla calculation, with your consent)</li>
                    <li>Push notification data (if enabled)</li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">3. How We Use Your Data</h2>
                <p className="text-stone-600 leading-relaxed">We use your data to:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Provide and maintain our service</li>
                  <li>Personalize your spiritual experience</li>
                  <li>Track your spiritual progress</li>
                  <li>Send prayer reminders (if enabled)</li>
                  <li>Display accurate prayer times for your location</li>
                  <li>Improve our application</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">4. Data Storage and Security</h2>
                <p className="text-stone-600 leading-relaxed">
                  Your data is stored securely via the Supabase platform which uses encryption and modern security standards. 
                  We implement appropriate technical and organizational measures to protect your personal data from unauthorized access, 
                  loss, or destruction.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">5. Data Sharing</h2>
                <p className="text-stone-600 leading-relaxed">
                  <strong>We do not sell or share your personal data with third parties</strong> except in the following cases:
                </p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Service providers that help us operate the app (Supabase for database, Aladhan API for prayer times)</li>
                  <li>When required by law</li>
                  <li>With your explicit consent</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">6. Google Sign-In</h2>
                <p className="text-stone-600 leading-relaxed">
                  If you choose to sign in with your Google account, we receive limited information from your Google profile 
                  (email address and name). We only access data necessary for authentication and do not access your contacts, 
                  calendars, or other Google services.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">7. Your Rights</h2>
                <p className="text-stone-600 leading-relaxed">You have the right to:</p>
                <ul className="list-disc list-inside text-stone-600 space-y-2 ml-4">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Withdraw consent for data processing</li>
                  <li>Export your data</li>
                </ul>
                <p className="text-stone-600 leading-relaxed mt-4">
                  To exercise these rights, contact us at the email listed in the "Contact" section.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">8. Cookies and Local Storage</h2>
                <p className="text-stone-600 leading-relaxed">
                  We use browser local storage to save your settings and session. We do not use cookies for tracking or advertising.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">9. Changes to This Policy</h2>
                <p className="text-stone-600 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of significant changes via the application or email.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-heading font-semibold text-[#292524]">10. Contact</h2>
                <p className="text-stone-600 leading-relaxed">
                  If you have questions about this Privacy Policy or how we handle your data, contact us at:
                </p>
                <div className="bg-stone-50 p-4 rounded-xl mt-4">
                  <p className="text-stone-700 font-medium">Email: app@islam-iznutra.com</p>
                </div>
              </section>
            </>
          )}

          {/* Footer Links */}
          <div className="pt-8 border-t border-stone-200 flex flex-wrap gap-4">
            <Link to="/terms" className="text-[#6B7B3F] hover:underline">
              {lang === 'hr' ? 'Uvjeti korištenja' : 'Terms of Service'}
            </Link>
            <Link to="/" className="text-[#6B7B3F] hover:underline">
              {lang === 'hr' ? 'Povratak na početnu' : 'Back to Home'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
